const {
  setCookie,
  deleteCookie,
  getCookie,
  setItemInSessionStorage,
  removeItemInSessionStorage,
  removeItemInLocalStorage,
} = require('./lib/browserData');
const agentLogin = require('./apis/agentLogin');
const agentLogout = require('./apis/agentLogout');
const { COOKIES, LOGIN_STATUS, SESSION_STORAGE, LOCAL_STORAGE } = require('./constants');
const agentAuthValidate = require('./apis/agentAuthValidate');
const impersonationLogin = require('./apis/impersonationLogin');
const { getCartCount } = require('./catalystActions');

const INVALID_PARAMETER_COMBINATION_ERROR = {
  code: 'AGENT_LOGIN_ERROR_INVALID_PARAMETER_COMBINATION',
  message: 'adfsAccessToken and adfsIdToken are mandatory fields',
};

function setAgentCookies(firstName, lastName, tenantId, expires, domainName) {
  const agentDataExpiry = expires || COOKIES.AGENT_DATA.EXPIRY;
  setCookie(
    COOKIES.AGENT_DATA.VALUE,
    `${encodeURIComponent(firstName)}*${encodeURIComponent(lastName)}`,
    tenantId,
    agentDataExpiry,
    true,
    domainName,
  );
}

function setKioskoCookies(
  documentNumber,
  storeLocalName,
  storeLocalNumber,
  agentId,
  isKioskoValue,
  userName,
  tenantId,
  expires,
  domainName,
  storeId,
  modulo,
) {
  const kioskoDataExpiry = expires || COOKIES.KIOSKO_DATA.EXPIRY;
  setCookie(
    COOKIES.KIOSKO_DATA.VALUE,
    `${encodeURIComponent(documentNumber)}*${encodeURIComponent(
      storeLocalName,
    )}*${encodeURIComponent(storeLocalNumber)}*${encodeURIComponent(agentId)}*${encodeURIComponent(
      isKioskoValue,
    )}*${encodeURIComponent(userName)}`,
    tenantId,
    kioskoDataExpiry,
    true,
    domainName,
  );

  if (storeId && modulo) {
    setCookie(
      COOKIES.KIOSKO_STORE_ID.VALUE,
      storeId[0],
      tenantId,
      COOKIES.KIOSKO_STORE_ID.EXPIRY,
      true,
      domainName,
    );
    setCookie(
      COOKIES.KIOSKO_MODULO.VALUE,
      modulo[0],
      tenantId,
      COOKIES.KIOSKO_MODULO.EXPIRY,
      true,
      domainName,
    );
  }
}

function removeCookies(tenantId, domainName) {
  deleteCookie(COOKIES.AGENT_SESSION.VALUE, tenantId, domainName);
  deleteCookie(COOKIES.AGENT_DATA.VALUE, tenantId, domainName);
  deleteCookie(COOKIES.KIOSKO_DATA.VALUE, tenantId, domainName);
  deleteCookie(COOKIES.SESSION.VALUE, tenantId, domainName);
  deleteCookie(COOKIES.CART_ID.VALUE, tenantId, domainName);
  deleteCookie(COOKIES.USER_ID.VALUE, tenantId, domainName);
  deleteCookie(COOKIES.CMR_POINTS, tenantId, domainName);
  deleteCookie(COOKIES.USER_FPAY_LINK_STATUS.VALUE, tenantId, domainName);
  setCookie(
    COOKIES.DYNAMIC_DATA.VALUE,
    '*0',
    tenantId,
    COOKIES.DYNAMIC_DATA.EXPIRY,
    true,
    domainName,
  );
  setCookie(
    COOKIES.CATALYST_DATA.VALUE,
    '*0',
    tenantId,
    COOKIES.CATALYST_DATA.EXPIRY,
    true,
    domainName,
  );
  removeItemInSessionStorage(SESSION_STORAGE.CATALYST_EMAIL_ID);
  removeItemInSessionStorage(SESSION_STORAGE.CATALYST_USER_TYPE);
  removeItemInLocalStorage(LOCAL_STORAGE.USER_FPAY_LINK_TOKEN);
  removeItemInSessionStorage(SESSION_STORAGE.DIGITAL_DATA);
}

function setUserCookies(firstName, lastName, cartCount, userId, tenantId, domainName) {
  setCookie(
    COOKIES.DYNAMIC_DATA.VALUE,
    `${encodeURIComponent(firstName)}*${cartCount || 0}*${encodeURIComponent(lastName)}`,
    tenantId,
    COOKIES.DYNAMIC_DATA.EXPIRY,
    true,
    domainName,
  );
  setCookie(
    COOKIES.CATALYST_DATA.VALUE,
    `${encodeURIComponent(firstName)}*${cartCount || 0}*${encodeURIComponent(lastName)}`,
    tenantId,
    COOKIES.CATALYST_DATA.EXPIRY,
    true,
    domainName,
  );
  setCookie(COOKIES.USER_ID.VALUE, userId, tenantId, COOKIES.USER_ID.EXPIRY, true, domainName);
}

async function login(
  adfsAccessToken,
  adfsRefreshToken,
  adfsIdToken,
  customInfo,
  tenantId,
  agentBaseUrl,
  isKiosko = false,
  additionalInfo = {},
) {
  if (!adfsAccessToken || !adfsIdToken) {
    return {
      loginStatus: false,
      data: undefined,
      errors: [INVALID_PARAMETER_COMBINATION_ERROR],
    };
  }
  const { whitelabel = {} } = additionalInfo;

  const {
    data,
    data: { sessionId } = {},
    errors,
  } = await agentLogin(
    adfsAccessToken,
    adfsRefreshToken,
    adfsIdToken,
    customInfo,
    tenantId,
    agentBaseUrl,
    additionalInfo,
  );

  if (!data) return { loginStatus: false, data: undefined, errors };
  const {
    user: { userName: { firstName, lastName1: lastName } = {} },
    expires,
  } = data;
  const agentSessionExpiry = expires || COOKIES.AGENT_SESSION.EXPIRY;
  setCookie(
    COOKIES.AGENT_SESSION.VALUE,
    sessionId,
    tenantId,
    agentSessionExpiry,
    true,
    whitelabel.domainName,
  );

  if ([firstName, lastName].includes(undefined)) {
    return {
      loginStatus: true,
      data,
      errors: [
        {
          code: 'AGENT_LOGIN_ERROR_MISSING_DATA',
          message: 'Missing information to set cookie',
        },
      ],
    };
  }

  if (isKiosko && customInfo) {
    deleteCookie(COOKIES.AGENT_DATA.VALUE, tenantId, whitelabel.domainName);
    const [
      documentNumber,
      storeLocalName,
      agentId,
      storeLocalNumber,
      isKioskoValue,
      userName,
      storeId,
      modulo,
    ] = customInfo;
    setKioskoCookies(
      documentNumber.values,
      storeLocalName.values,
      storeLocalNumber.values,
      agentId.values,
      isKioskoValue.values,
      userName.values,
      tenantId,
      expires,
      whitelabel.domainName,
      storeId ? storeId.values : undefined,
      modulo ? modulo.values : undefined,
    );
  } else {
    deleteCookie(COOKIES.KIOSKO_DATA.VALUE, tenantId, whitelabel.domainName);
    setAgentCookies(firstName, lastName, tenantId, expires, whitelabel.domainName);
  }

  return {
    loginStatus: true,
    data,
    errors: [],
  };
}

async function logout(tenantId, agentBaseUrl, additionalInfo = {}) {
  const { whitelabel = {} } = additionalInfo;
  const response = await agentLogout(tenantId, agentBaseUrl, additionalInfo);

  if (!response.success) return response;
  removeCookies(tenantId, whitelabel.domainName);
  return response;
}

async function isLoggedIn(baseUrl, cookies, channelId, tenantId) {
  const sessionId = getCookie(COOKIES.AGENT_SESSION.VALUE, cookies);
  if (!sessionId) {
    return false;
  }
  const { active, status } = await agentAuthValidate(baseUrl, sessionId, tenantId, channelId);
  return active && status === LOGIN_STATUS.AUTHENTICATED;
}

async function userImpersonationLogin(
  emailAddress,
  tenantId,
  agentBaseUrl,
  flow,
  additionalInfo = {},
) {
  if (!flow) {
    deleteCookie(COOKIES.CART_ID.VALUE, tenantId);
  }
  const { whitelabel = {} } = additionalInfo;
  const {
    data,
    data: { sessionId } = {},
    errors,
  } = await impersonationLogin(emailAddress, tenantId, agentBaseUrl, additionalInfo);

  if (!data) {
    setItemInSessionStorage(SESSION_STORAGE.CATALYST_EMAIL_ID, emailAddress);
    setItemInSessionStorage(SESSION_STORAGE.CATALYST_USER_TYPE, LOGIN_STATUS.GUEST);
    return { loginStatus: false, data: undefined, errors };
  }

  const {
    session: { status: sessionStatus },
    user: { userName: { firstName, lastName1: lastName } = {}, userId },
  } = data;
  setItemInSessionStorage(SESSION_STORAGE.CATALYST_EMAIL_ID, emailAddress);
  setItemInSessionStorage(SESSION_STORAGE.CATALYST_USER_TYPE, sessionStatus);
  setCookie(
    COOKIES.SESSION.VALUE,
    sessionId,
    tenantId,
    COOKIES.SESSION.EXPIRY,
    true,
    whitelabel.domainName,
  );
  const { cartCount, errors: cartCountErrors } = await getCartCount(
    tenantId,
    agentBaseUrl,
    additionalInfo,
  );
  if ([firstName, lastName].includes(undefined)) {
    return {
      loginStatus: true,
      data,
      errors: [
        ...cartCountErrors,
        {
          code: 'CATALYST_LOGIN_ERROR_MISSING_DATA',
          message: 'Missing information to set cookie',
        },
      ],
    };
  }
  setUserCookies(firstName, lastName, cartCount, userId, tenantId, whitelabel.domainName);

  return { loginStatus: true, data, errors };
}

module.exports = {
  login,
  logout,
  isLoggedIn,
  userImpersonationLogin,
};
